import { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { SECTIONS } from './runtime/formularioSections'
import { calculateScores, estimateFitAndRetention, getProfileArchetype, clamp, uid } from './runtime/formularioRuntimeUtils'
import { useNotification } from '../../context/NotificationContext'
import ApiService from '../../services/api'

// Mínimo usado quando a pergunta discursiva não define "minChars" em formularioSections.js.
const MIN_DISCURSIVE_LENGTH = 350
const minCharsOf = (question) => (Number.isFinite(question?.minChars) && question.minChars > 0 ? question.minChars : MIN_DISCURSIVE_LENGTH)
const DISCURSIVE_QUESTIONS = SECTIONS.filter((s) => s.type === 'discursive').flatMap((s) => s.questions)
const TOTAL_QUESTIONS = SECTIONS.reduce((acc, current) => {
  if (current.type === 'likert') return acc + current.items.length
  if (current.type === 'sjt') return acc + current.scenarios.length * 2
  if (current.type === 'discursive') return acc + current.questions.length
  return acc
}, 0)

const LikertItem = memo(function LikertItem({ item, idx, scale, scaleLabels, selectedValue, onSelect, registerFieldRef }) {
  return (
    <div className="assessment-card likert-item" ref={(el) => { registerFieldRef(item.id, el) }} tabIndex={-1}>
      <p className="likert-question"><span>{idx + 1}.</span> {item.text}</p>
      <div className="likert-group">
        {scale.map((val, scaleIdx) => (
          <button key={val} type="button" className={selectedValue === val ? 'active' : ''} onClick={() => onSelect(item.id, val)}>
            <strong>{val}</strong>
            <span>{scaleLabels?.[scaleIdx] || (val <= 2 ? 'Discordo' : val === 3 ? 'Neutro' : 'Concordo')}</span>
          </button>
        ))}
      </div>
    </div>
  )
})

const DiscursiveQuestion = memo(function DiscursiveQuestion({ question, value, onChange, registerFieldRef }) {
  const length = value.trim().length
  return (
    <div className="field">
      <label>{question.title}</label>
      {question.text && <p className="assessment-copy">{question.text}</p>}
      <textarea ref={(el) => { registerFieldRef(question.id, el) }} value={value} onChange={(e) => onChange(question.id, e.target.value)} />
      <small>{length}/{minCharsOf(question)} caracteres mínimos</small>
    </div>
  )
})

export default function AssessmentFlow({ candidate, area, consent, onClose }) {
  const { showWarning, showSuccess } = useNotification()
  const [step, setStep] = useState('intro')
  const [sectionIdx, setSectionIdx] = useState(0)
  const [answers, setAnswers] = useState({})
  const [sjtAnswers, setSjtAnswers] = useState({})
  const [discursive, setDiscursive] = useState({})
  const [report, setReport] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [captcha, setCaptcha] = useState(() => {
    const a = Math.floor(Math.random() * 8) + 2
    const b = Math.floor(Math.random() * 8) + 2
    return { a, b, answer: '' }
  })
  const fieldRefs = useRef({})

  const mainRef = useRef(null)
  const sidebarRef = useRef(null)

  const section = SECTIONS[sectionIdx];

  useLayoutEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    sidebarRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [sectionIdx, step])

  const registerFieldRef = useCallback((fieldId, element) => {
    fieldRefs.current[fieldId] = element
  }, [])

  const sectionPreview = useMemo(() => (
    SECTIONS.map((current) => ({
      id: current.id,
      title: current.title,
      subtitle: current.subtitle,
    }))
  ), [])

  const progress = useMemo(() => {
    const answeredLikert = Object.values(answers).filter((value) => typeof value === 'number').length
    const answeredSjt = Object.values(sjtAnswers).reduce((acc, current) => {
      let totalAnswers = acc
      if (current?.best) totalAnswers += 1
      if (current?.worst) totalAnswers += 1
      return totalAnswers
    }, 0)
    const answeredDiscursive = DISCURSIVE_QUESTIONS.filter((question) => (discursive[question.id] || '').trim().length >= minCharsOf(question)).length
    const answered = answeredLikert + answeredSjt + answeredDiscursive

    return {
      percentage: TOTAL_QUESTIONS ? Math.round((answered / TOTAL_QUESTIONS) * 100) : 0,
    }
  }, [answers, sjtAnswers, discursive])

  const handleLikertSelect = useCallback((itemId, value) => {
    setAnswers((prev) => {
      if (prev[itemId] === value) return prev
      return { ...prev, [itemId]: value }
    })
  }, [])

  const handleDiscursiveChange = useCallback((questionId, value) => {
    setDiscursive((prev) => {
      if (prev[questionId] === value) return prev
      return { ...prev, [questionId]: value }
    })
  }, [])

  const focusField = (fieldId) => {
    const target = fieldRefs.current[fieldId]
    target?.focus()
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const validateCurrentSection = () => {
    if (section.type === 'likert') {
      const missing = section.items.find((item) => typeof answers[item.id] !== 'number')
      if (missing) {
        showWarning('Responda todas as perguntas desta etapa antes de avançar.')
        focusField(missing.id)
        return false
      }
    }

    if (section.type === 'sjt') {
      const incomplete = section.scenarios.find((scenario) => {
        const scenarioAnswer = sjtAnswers[scenario.id] || {}
        return !scenarioAnswer.best || !scenarioAnswer.worst
      })

      if (incomplete) {
        showWarning('Em cada cenário, selecione a melhor e a pior alternativa.')
        focusField(incomplete.id)
        return false
      }
    }

    if (section.type === 'discursive') {
      const invalid = section.questions.find((question) => (discursive[question.id] || '').trim().length < minCharsOf(question))
      if (invalid) {
        showWarning(`A resposta "${invalid.title}" precisa ter pelo menos ${minCharsOf(invalid)} caracteres.`)
        focusField(invalid.id)
        return false
      }
    }

    return true
  }

  const finish = async () => {
    if (!validateCurrentSection()) return
    const expectedCaptcha = captcha.a + captcha.b
    if (Number(captcha.answer) !== expectedCaptcha) {
      showWarning('Validação anti-bot inválida. Resolva a conta corretamente para enviar.')
      return
    }

    const scores = calculateScores(SECTIONS, answers, sjtAnswers)
    const profile = getProfileArchetype(scores)
    const fitMetrics = estimateFitAndRetention(scores, area)

    const generated = {
      id: uid(),
      createdAt: new Date().toISOString(),
      candidate: { ...candidate, area, consentimentoLgpd: Boolean(consent) },
      answers,
      sjtAnswers,
      discursive,
      scores,
      profile,
      metrics: {
        aderencia: fitMetrics.fit,
        permanencia: fitMetrics.retention,
        fitRationale: fitMetrics.fitRationale,
        retentionRationale: fitMetrics.retentionRationale,
      },
    }

    setSubmitting(true)
    const response = await ApiService.submitFormularioPublico(generated)
    setSubmitting(false)

    if (!response.success) {
      showWarning(response.error || 'Não foi possível enviar o questionário.')
      return
    }

    setReport({
      ...generated,
      protocolo: response.data?.protocolo || response.data?.Protocolo || generated.id,
      // Com a prova técnica automática ligada no RH, o backend devolve o link da prova do candidato.
      provaToken: response.data?.provaToken || null,
    })
    setStep('done')
    showSuccess('Questionário enviado com sucesso.')
  }

  const hasPrevious = useMemo(() => sectionIdx > 0, [sectionIdx])
  const canGoNext = useMemo(() => sectionIdx < SECTIONS.length - 1, [sectionIdx])

  const handleNext = () => {
    if (!validateCurrentSection()) return
    setSectionIdx((prev) => clamp(prev + 1, 0, SECTIONS.length - 1))
  }

  if (step === 'intro') {
    return (
      <div className="assessment-main" ref={mainRef} style={{ gridColumn: '1 / -1' }}>
        <div className="assessment-badge">Avaliação comportamental</div>
        <h2 className="assessment-title">Vamos começar</h2>
        <p className="assessment-copy">Você responderá perguntas objetivas e discursivas. Leva poucos minutos.</p>
        <div className="intro-sections">
          {sectionPreview.map((item) => (
            <article key={item.id} className="intro-section-card">
              <h3>{item.title}</h3>
              <p>{item.subtitle}</p>
            </article>
          ))}
        </div>
        <div className="actions-row">
          <button className="btn-ghost" type="button" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" type="button" onClick={() => setStep('quiz')}>Começar avaliação</button>
        </div>
      </div>
    )
  }

  if (step === 'done') {
    return (
      <div className="assessment-main" ref={mainRef} style={{ gridColumn: '1 / -1' }}>
        {report?.provaToken ? (
          <div className="thank-you">
            <h3>Questionário enviado!</h3>
            <p>Protocolo: <strong>{report.protocolo}</strong></p>
            <p>
              A próxima etapa do processo seletivo já está disponível: a prova técnica, preparada de acordo com o
              seu nível de experiência. Ela começa com uma página de orientações; leia com calma antes de iniciar.
            </p>
            <button
              className="btn-primary"
              type="button"
              onClick={() => { window.location.href = `/formulario?prova=${encodeURIComponent(report.provaToken)}` }}
            >
              Seguir para a prova técnica
            </button>
          </div>
        ) : (
          <div className="thank-you">
            <h3>Obrigado!</h3>
            <p>Seu questionário foi enviado com sucesso.</p>
            <p>Protocolo: <strong>{report?.protocolo}</strong></p>
            <button className="btn-primary" type="button" onClick={onClose}>Fechar</button>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <aside className="assessment-sidebar" ref={sidebarRef}>
        <div className="assessment-sidebar-card candidate-summary">
          <div className="assessment-badge">Avaliação veiga</div>
          <h2 className="assessment-title">Questionário comportamental</h2>
          <p className="assessment-copy">Fluxo direto: os dados do candidato são preenchidos na entrada e o questionário já começa nesta tela.</p>
          <div className="candidate-summary-card">
            <h3>{candidate?.name || 'Nome não informado'}</h3>
            <p>{area ? `Área: ${area}` : 'Área não informada'}</p>
            <p>{candidate?.phone || 'Telefone não informado'}</p>
            <div className="progress-track"><span style={{ width: `${progress.percentage}%` }} /></div>
            <strong>{progress.percentage}% preenchido</strong>
          </div>
        </div>
      </aside>
      <div className="assessment-main" ref={mainRef}>
        {section.subtitle && <p className="assessment-subtitle">{section.subtitle}</p>}
        <h2 className="assessment-title">{section.title}</h2>
        <p className="assessment-copy">{section.description}</p>

        {section.type === 'likert' && section.items.map((item, idx) => (
          <LikertItem
            key={item.id}
            item={item}
            idx={idx}
            scale={section.scale}
            scaleLabels={section.scaleLabels}
            selectedValue={answers[item.id]}
            onSelect={handleLikertSelect}
            registerFieldRef={registerFieldRef}
          />
        ))}

        {section.type === 'sjt' && section.scenarios.map((sc) => (
          <div key={sc.id} className="assessment-card" ref={(el) => { registerFieldRef(sc.id, el) }} tabIndex={-1}>
            <p>{sc.situation}</p>
            {sc.options.map((opt) => {
              const bestSelected = sjtAnswers[sc.id]?.best === opt.id
              const worstSelected = sjtAnswers[sc.id]?.worst === opt.id
              return <div key={opt.id} className={`sjt-option ${bestSelected ? 'best' : ''} ${worstSelected ? 'worst' : ''}`.trim()}><span>{opt.text}</span><div className="sjt-actions"><button type="button" className={bestSelected ? 'active-best' : ''} onClick={() => setSjtAnswers((prev) => ({ ...prev, [sc.id]: { ...(prev[sc.id] || {}), best: opt.id } }))}>Melhor</button><button type="button" className={worstSelected ? 'active-worst' : ''} onClick={() => setSjtAnswers((prev) => ({ ...prev, [sc.id]: { ...(prev[sc.id] || {}), worst: opt.id } }))}>Pior</button></div></div>
            })}
          </div>
        ))}

        {section.type === 'discursive' && section.questions.map((q) => (
          <DiscursiveQuestion
            key={q.id}
            question={q}
            value={discursive[q.id] || ''}
            onChange={handleDiscursiveChange}
            registerFieldRef={registerFieldRef}
          />
        ))}

        {!canGoNext && (
          <div className="assessment-card" style={{ marginTop: 16 }}>
            <h4>Validação anti-bot</h4>
            <p>Quanto é <strong>{captcha.a} + {captcha.b}</strong>?</p>
            <input
              type="number"
              value={captcha.answer}
              onChange={(e) => setCaptcha((prev) => ({ ...prev, answer: e.target.value }))}
              placeholder="Digite o resultado"
            />
          </div>
        )}

        <div className="actions-row">
          {hasPrevious && <button type="button" className="btn-ghost" onClick={() => setSectionIdx((prev) => clamp(prev - 1, 0, SECTIONS.length - 1))}>Etapa anterior</button>}
          {canGoNext ? <button type="button" className="btn-primary" onClick={handleNext}>Próxima etapa</button> : <button type="button" className="btn-primary" onClick={finish} disabled={submitting}>{submitting ? 'Enviando...' : 'Finalizar avaliação'}</button>}
        </div>
      </div>
    </>
  )
}
